import React, { useState } from "react";

import Select, {
  SelectChangeEvent,
} from "../../../../components/form-item/Select";
import DateInput from "../../../../components/calendar/DateInput";
import DateDisplay, {
  DATE_DISPLAY_TYPE,
} from "../../../../components/date-display/DateDisplay";
import IconButton from "../../../../components/button/IconButton";
import DropdownMenu, {
  type DropdownMenuItem,
} from "../../../../components/dropdown-menu/DropdownMenu";
import ToolTip from "../../../../components/tooltip/ToolTip";
import RatingCell from "./RatingCell";
import type { Shot } from "../../../../app/services/shots";
import {
  getClientTableStatusOptions,
  getShotTableStatusKey,
  getTableStatusOptions,
  isDeliveryDateEditable,
  shotHasMedia,
  SHOT_TABLE_STATUS_LABELS,
  TABLE_STATUS_TO_SHOT_STATUS,
  ShotTableStatusKey,
} from "../../../../constant/shotTableStatus";
import {
  formatTableDate,
  isTabularClipboardText,
  toDeliveryDateParam,
  ShotPatch,
} from "./shotTableUtils";

const DATE_INPUT_OPTIONS = { static: false } as const;

/**
 * セル内編集用 input の高密度スタイル。
 * 枠は持たず、編集中の表示はセル側の :focus-within リングに任せる
 * （グローバル input の padding / border / focus シャドウを上書き）。
 */
const NAME_INPUT_STYLE: React.CSSProperties = {
  display: "block",
  width: "100%",
  padding: "1px 6px",
  fontSize: 13,
  lineHeight: "150%",
  height: 24,
  border: "none",
  outline: "none",
  boxShadow: "none",
  background: "transparent",
};

interface ShotTableRowProps {
  shot: Shot;
  rowIndex: number;
  /** プロデューサー側のフル編集権限（納品日・全ステータス・全行削除） */
  canEdit: boolean;
  /**
   * クライアント側の限定編集権限。
   * ショット名の編集・準備中行の削除・限定ステータス遷移
   * （チェックバック / 保留 → 完了、保留 / 完了 → チェックバック）のみ許可する。
   */
  canClientEdit: boolean;
  /** 自分の評価をセル内で入力できるか（クライアント系 & can_rate） */
  canRate: boolean;
  isClientGroup: boolean;
  /**
   * ショット間移動メニュー（3点リーダー）を表示するか。
   * プロデューサー側のフル編集権限またはクライアント責任者（CLIENT）のみ。
   */
  canMove: boolean;
  onPatch: (shotId: string, patch: ShotPatch) => void;
  onRequestDelete: (shot: Shot) => void;
  /** 3点リーダー「ショット間の移動」で移動ウィザードを開く */
  onRequestMove: (shot: Shot) => void;
  onRate: (shot: Shot, rating: number) => void;
  onClearRating: (shot: Shot) => void;
  /** 行頭のリンクから Preview ページへ遷移する */
  onOpenPreview: (shot: Shot) => void;
}

const ShotTableRow: React.FC<ShotTableRowProps> = ({
  shot,
  rowIndex,
  canEdit,
  canClientEdit,
  canRate,
  isClientGroup,
  canMove,
  onPatch,
  onRequestDelete,
  onRequestMove,
  onRate,
  onClearRating,
  onOpenPreview,
}) => {
  const [isEditingName, setIsEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(shot.shot_name);

  const statusKey = getShotTableStatusKey(shot);
  const hasMedia = shotHasMedia(shot);
  // ステータス起因で納品日を使わない（チェックバック・保留・完了）かどうか。
  // 該当セルは値を表示せずダークグレーで沈める（権限による読み取り専用とは区別する）
  const dateLocked = !isDeliveryDateEditable(statusKey);
  const isCompleted = statusKey === "completed";
  const isOnHold = statusKey === "on_hold";

  // ── 権限ごとの操作可否 ──
  const canEditName = canEdit || canClientEdit;
  // クライアントは限定遷移のみ。遷移先がない（準備中・制作中）は表示のみ
  const statusOptions = canEdit
    ? getTableStatusOptions(hasMedia)
    : canClientEdit
      ? getClientTableStatusOptions(statusKey)
      : [];
  const canEditStatus = statusOptions.length > 0;
  // クライアントはやり直しが容易な準備中（テイク未納品）の行のみ削除できる
  const canDeleteRow = canEdit || (canClientEdit && statusKey === "preparing");
  // 行の操作はアイコンを並べず 3 点リーダーに集約する
  const rowActions: DropdownMenuItem[] = [
    ...(canMove
      ? [
          {
            label: "ショット間の移動",
            icon: "drive_file_move",
            onClick: () => onRequestMove(shot),
          },
        ]
      : []),
    ...(canDeleteRow
      ? [
          {
            label: "ショットを削除",
            icon: "delete",
            onClick: () => onRequestDelete(shot),
          },
        ]
      : []),
  ];
  // 完了行はダーク背景（白文字）のため、薄グレー文字クラスは外して行の色を継承させる
  const subTextClass = isCompleted
    ? "bodytext-13"
    : "bodytext-13 u-text-light-gray";

  const rowClassName = isCompleted
    ? "table-row-muted"
    : isOnHold
      ? "table-row-dimmed"
      : undefined;

  // ── ショット名 ──
  const startEditName = () => {
    if (!canEditName) return;
    setNameDraft(shot.shot_name);
    setIsEditingName(true);
  };

  const commitName = () => {
    setIsEditingName(false);
    const trimmed = nameDraft.trim();
    if (!trimmed || trimmed === shot.shot_name) return;
    onPatch(shot.id, { shot_name: trimmed });
  };

  const handleNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.nativeEvent.isComposing) {
      e.currentTarget.blur();
    } else if (e.key === "Escape") {
      setNameDraft(shot.shot_name);
      setIsEditingName(false);
    }
  };

  // 複数セルの貼り付けはテーブル全体の onPaste（親）に委ねる。
  // 編集中の下書きは破棄して、貼り付けによる上書きと競合しないようにする。
  const handleNamePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData("text/plain");
    if (isTabularClipboardText(text)) {
      setNameDraft(shot.shot_name);
      setIsEditingName(false);
    }
  };

  // ── ステータス ──
  const handleStatusChange = (e: SelectChangeEvent) => {
    const key = e.target.value as ShotTableStatusKey;
    if (key === statusKey) return;
    // ステータス変更時は next_delivery_date が自動クリアされる（既存仕様に合わせる）
    onPatch(shot.id, { status: TABLE_STATUS_TO_SHOT_STATUS[key] });
  };

  // ── 次の納品日 ──
  const handleDateChange = (e: { target: { value: Date | Date[] | null } }) => {
    const value = e.target.value;
    if (!(value instanceof Date)) return;
    const next = new Date(value);
    const prev = shot.next_delivery_date
      ? new Date(shot.next_delivery_date)
      : null;
    if (prev) {
      next.setHours(prev.getHours(), prev.getMinutes(), 0, 0);
    } else {
      next.setHours(10, 0, 0, 0);
    }
    const iso = toDeliveryDateParam(next);
    if (iso === shot.next_delivery_date) return;
    onPatch(shot.id, { next_delivery_date: iso });
  };

  return (
    // 完了はダークグレー・保留は薄いグレーで行全体を沈め、「動きがない行」を見分けやすくする
    <tr data-row-index={rowIndex} className={rowClassName}>
      {/* Preview ページへのリンク */}
      <td style={{ textAlign: "center" }}>
        <IconButton
          icon="open_in_new"
          size={16}
          title="プレビューを開く"
          ariaLabel="プレビューを開く"
          onClick={() => onOpenPreview(shot)}
        />
      </td>

      {/* ショット名 */}
      <td>
        {isEditingName ? (
          <input
            type="text"
            autoFocus
            value={nameDraft}
            onChange={(e) => setNameDraft(e.target.value)}
            onBlur={commitName}
            onKeyDown={handleNameKeyDown}
            onPaste={handleNamePaste}
            style={NAME_INPUT_STYLE}
          />
        ) : (
          <ToolTip description={shot.shot_name} truncatedOnly>
            <div
              className="bodytext-13 u-text-ellipsis u-white-space"
              translate="no"
              onClick={startEditName}
              style={{
                minHeight: 22,
                lineHeight: "22px",
                cursor: canEditName ? "text" : "default",
              }}
            >
              {shot.shot_name}
            </div>
          </ToolTip>
        )}
      </td>

      {/* ステータス（ポップオーバーをクリップしないよう overflow を解除） */}
      <td className="table-cell-visible">
        {canEditStatus ? (
          <Select
            name={`shot-status-${shot.id}`}
            size="small"
            options={statusOptions}
            value={statusKey}
            onChange={handleStatusChange}
          />
        ) : (
          <span className="bodytext-13">
            {SHOT_TABLE_STATUS_LABELS[statusKey]}
          </span>
        )}
      </td>

      {/* 次の納品日（準備中・制作中のみ。チェックバック・保留・完了は値も出さずダークセルで沈める） */}
      <td className={dateLocked ? "table-cell-muted" : "table-cell-visible"}>
        {!dateLocked &&
          (canEdit ? (
            <DateInput
              placeholder="未設定"
              size="small"
              value={
                shot.next_delivery_date
                  ? new Date(shot.next_delivery_date)
                  : undefined
              }
              dateFormat="Y/n/j"
              options={DATE_INPUT_OPTIONS}
              onChange={handleDateChange}
            />
          ) : (
            shot.next_delivery_date && (
              <span className={subTextClass}>
                {formatTableDate(shot.next_delivery_date)}
              </span>
            )
          ))}
      </td>

      {/* 最終更新日 */}
      <td>
        <span className={subTextClass}>
          <DateDisplay
            date={shot.updated_at}
            type={DATE_DISPLAY_TYPE.DATE_TIME}
          />
        </span>
      </td>

      {/* 総メディア数 */}
      <td style={{ textAlign: "right" }}>
        {shot.media_item_count > 0 && (
          <span className={subTextClass}>{shot.media_item_count}</span>
        )}
      </td>

      {/* 最大テイク数（Preview のバリエーション名グループに合わせる） */}
      <td style={{ textAlign: "right" }}>
        {shot.max_take_count != null && shot.max_take_count > 0 && (
          <span className={subTextClass}>{shot.max_take_count}</span>
        )}
      </td>

      {/* 評価（クライアントはセル内の星を直接クリック、プロデューサーは平均表示） */}
      <td>
        <RatingCell
          shot={shot}
          canRate={canRate}
          isClientGroup={isClientGroup}
          onRate={onRate}
          onClear={onClearRating}
        />
      </td>

      {/* 行アクション（移動・削除を 3 点リーダーに畳む。
          ポップオーバーをクリップしないよう overflow を解除） */}
      {(canEdit || canClientEdit) && (
        <td className="table-cell-visible">
          <div className="u-row u-items-center u-justify-end">
            {rowActions.length > 0 && <DropdownMenu items={rowActions} />}
          </div>
        </td>
      )}
    </tr>
  );
};

export default ShotTableRow;
